// oxlint-disable react/jsx-pascal-case
import { type Component, type ComponentProps, type JSX, Show } from "solid-js";
import { cn } from "@apunte-digital/shared";

interface EmptyStateProps {
  icon: Component<ComponentProps<"svg">>;
  title: string;
  description?: string;
  /**
   * Optional editorial SVG illustration. When supplied, it replaces the icon
   * badge and becomes the visual focus of the empty state (see
   * `components/ui/illustrations.tsx`). Theme-aware and decorative.
   */
  illustration?: JSX.Element;
  /** Optional call-to-action rendered below the description (e.g. a button). */
  action?: JSX.Element;
  /**
   * When provided, the illustrative mark (badge or SVG illustration) becomes an
   * interactive target that triggers the same action as the primary CTA (open a
   * dialog, navigate, or focus an input) so the empty state never feels like a
   * dead end. Tapping the mark is a discoverable shortcut to the CTA — leave it
   * undefined for purely informational empty states (the mark stays decorative).
   */
  onIconActivate?: () => void;
  /**
   * Accessible label for the interactive mark (required when `onIconActivate`
   * is set). Should describe the action, e.g. "Create your first page".
   */
  iconActionLabel?: string;
  class?: string;
}

/**
 * Editorial empty state for Apunte Digital: a "paper" surface with the icon
 * tinted sienna (or a supplied SVG illustration), a serif title, and a legible
 * description. The entry fades with `prefers-reduced-motion`.
 */
export const EmptyState: Component<EmptyStateProps> = (props) => {
  // Visual content of the mark (editorial SVG illustration or the sienna-tinted
  // icon badge). It is reused in both the decorative and interactive branches
  // to avoid duplicating the markup.
  const mark = (): JSX.Element => (
    <Show
      when={props.illustration}
      fallback={
        <div class="flex h-20 w-20 items-center justify-center rounded-2xl bg-surface ring-1 ring-border shadow-sm shadow-accent/5">
          <props.icon class="h-9 w-9 text-accent" />
        </div>
      }
    >
      <div class="text-accent motion-safe:animate-scale-in">{props.illustration}</div>
    </Show>
  );

  return (
    <div
      class={cn(
        "flex flex-1 flex-col items-center justify-center px-6 py-12 text-center motion-safe:animate-fade-in",
        props.class,
      )}
    >
      {/* When there is a wired action, the mark becomes a real <button> so
          que tap/click/teclado (Enter/Space, nativos en <button>) disparen el CTA.
          Si no, queda como contenedor decorativo. El anillo de foco es visible y
          el movimiento de hover respeta prefers-reduced-motion (motion-safe). */}
      <Show when={props.onIconActivate} fallback={<div class="mb-6">{mark()}</div>}>
        {(activate) => (
          <button
            type="button"
            aria-label={props.iconActionLabel ?? props.title}
            onClick={() => {
              activate()();
            }}
            class="group mb-6 rounded-2xl outline-none transition-transform duration-200 hover:opacity-95 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            {mark()}
          </button>
        )}
      </Show>
      <h3 class="font-serif text-xl font-semibold tracking-tight text-foreground">{props.title}</h3>
      <Show when={props.description}>
        <p class="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
          {props.description}
        </p>
      </Show>
      <Show when={props.action}>
        <div class="mt-6">{props.action}</div>
      </Show>
    </div>
  );
};
